import { cn } from '@/lib/utils/cn';
import { de } from '@/i18n/de';
import { asPercent, type PaceStatus } from '@/domain/goals/progress';

const PACE_FARBE: Record<PaceStatus, string> = {
  voraus: 'bg-ok',
  im_plan: 'bg-accent',
  hinten: 'bg-warn',
};

interface ProgressBarProps {
  /** 0..1 */
  value: number;
  /** 0..1 - wird als Markierung eingeblendet, wenn gesetzt. */
  timeMarker?: number;
  pace?: PaceStatus;
  /** Bereichsfarbe; ohne `pace` faerbt sie den Balken. */
  color?: string;
  label: string;
  className?: string;
}

/**
 * Fortschrittsbalken mit Zeitmarke.
 *
 * Die senkrechte Marke zeigt, wie weit die Zeit ist. Erst dadurch wird der
 * Balken lesbar: "40 %" heisst etwas anderes, je nachdem ob ein Zehntel oder
 * zwei Drittel des Zeitraums um sind.
 */
export function ProgressBar({
  value, timeMarker, pace, color, label, className,
}: ProgressBarProps) {
  const prozent = asPercent(value);

  return (
    <div
      role="progressbar"
      aria-valuenow={prozent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      data-area={color ? '' : undefined}
      style={color ? ({ '--area': color } as React.CSSProperties) : undefined}
      className={cn('relative h-1.5 w-full overflow-hidden rounded-full bg-line', className)}
    >
      <div
        className={cn('h-full rounded-full transition-[width]', pace ? PACE_FARBE[pace] : 'area-dot')}
        style={{ width: `${prozent}%` }}
      />
      {timeMarker !== undefined && (
        <span
          aria-hidden="true"
          title={de.goals.paceHint(prozent, asPercent(timeMarker))}
          className="absolute top-0 h-full w-px bg-ink-faint"
          style={{ left: `${asPercent(timeMarker)}%` }}
        />
      )}
    </div>
  );
}

/** Tempo als Wort - nicht nur als Farbe, damit es ohne Farbsehen lesbar ist. */
export function PaceLabel({ pace, progress, timeProgress }: {
  pace: PaceStatus; progress: number; timeProgress: number;
}) {
  const FARBE: Record<PaceStatus, string> = {
    voraus: 'text-ok',
    im_plan: 'text-ink-faint',
    hinten: 'text-warn',
  };

  return (
    <span
      className={cn('shrink-0 text-[11px] tabular-nums', FARBE[pace])}
      title={de.goals.paceHint(asPercent(progress), asPercent(timeProgress))}
    >
      {asPercent(progress)} % · {de.goals.pace[pace]}
    </span>
  );
}
